// Shared state for one system-prompt layer (#637). Three consumers — the ORG +
// USER editors on the Prompts route and the REPO editor on the repo settings
// tab — drive identical role/busy/error/refetch plumbing over a `SystemPrompt`
// query, so the state lives here once (SOLID: containers stay thin, one reason
// to change). Presenter = `SystemPromptEditor`.
//
// The ROLE (plan 101 slice 06) is part of the layer's address: the query is
// keyed by it and every save/clear is sent to the role showing when it was
// clicked, so a role switch can never write one role's draft into another's row.
//
// The DRAFT lives here too, per role, and not in the presenter: a role switch
// re-keys the query, the card re-mounts over the new role's read, and a draft
// held by the card was re-seeded from that read — an unsaved edit silently lost
// (review of #4472). A role's draft is dropped only when its save or clear lands.

import { PROMPT_ROLE_ALL } from '@developerz/domain';
import { type Accessor, createSignal } from 'solid-js';
import { createStore } from 'solid-js/store';
import { t } from '../i18n';
import type { PromptRoleChoice, SystemPrompt } from './system-prompts';
import { ago } from './time';

/** What the editor reads of a layer's query; the container keeps the full one. */
type PromptQuery = {
  data: SystemPrompt | null | undefined;
  refetch: () => Promise<unknown>;
};

export type PromptEditorState = {
  role: () => PromptRoleChoice;
  onRoleChange: (role: PromptRoleChoice) => void;
  value: () => string;
  /** The text being edited for the current role — its unsaved draft, else `value()`. */
  draft: () => string;
  onDraft: (text: string) => void;
  savedText: () => string;
  busy: () => boolean;
  error: () => string | null;
  onSave: (text: string) => void;
  onClear: () => void;
};

/** How one layer is read and written, each call addressed at a role. */
export type PromptLayerApi<Q extends PromptQuery> = {
  readonly read: (role: Accessor<PromptRoleChoice>) => Q;
  readonly save: (text: string, role: PromptRoleChoice) => Promise<unknown>;
  readonly clear: (role: PromptRoleChoice) => Promise<unknown>;
};

/** Wire a layer's role-keyed query to its PUT/DELETE, exposing the editor's props. */
export function usePromptLayer<Q extends PromptQuery>(
  api: PromptLayerApi<Q>,
): { query: Q; editor: PromptEditorState } {
  const [role, setRole] = createSignal<PromptRoleChoice>(PROMPT_ROLE_ALL);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [drafts, setDrafts] = createStore<Partial<Record<PromptRoleChoice, string>>>({});
  const query = api.read(role);

  const value = (): string => query.data?.text ?? '';
  const savedText = (): string => {
    const iso = query.data?.updated_at_iso;
    return iso ? t('system_prompts.saved_at', { when: ago(iso) }) : '';
  };

  const run = async (
    at: PromptRoleChoice,
    op: () => Promise<unknown>,
    msg: string,
  ): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await op();
      setDrafts(at, undefined);
    } catch {
      setError(msg);
    } finally {
      await query.refetch();
      setBusy(false);
    }
  };

  const editor: PromptEditorState = {
    role,
    onRoleChange: (next) => {
      setError(null);
      setRole(next);
    },
    value,
    draft: () => drafts[role()] ?? value(),
    onDraft: (text) => setDrafts(role(), text),
    savedText,
    busy,
    error,
    onSave: (text) => {
      const at = role();
      void run(at, () => api.save(text, at), t('system_prompts.save_error'));
    },
    onClear: () => {
      const at = role();
      void run(at, () => api.clear(at), t('system_prompts.clear_error'));
    },
  };
  return { query, editor };
}
